
import React, { useState, useEffect } from 'react';
import { useCart } from '../../../contexts/CartContext';

interface ProductVariant {
	id: string;
	size: string;
	price: number;
	compareAtPrice?: number;
	pricePerMeter: number;
	rollLength: number;
	variantImage?: string;
	variantImages?: string[];
	attributes?: Record<string, any>;
	quantity?: number;
	variantTitle?: string;
}

interface HierarchicalVariant {
	variantLevels: {
		level1: string;
		level2: string;
		level3: string;
	};
	attributes: Record<string, string>;
	sku: string;
	price: number;
	basePrice: number;
	quantity: number;
	isDefault: boolean;
	variantImages: string[];
	variantTitle?: string;
	variantDescription?: string;
	available?: boolean;
}

interface VariantSheetDesktopProps {
	product: any;
	variants: ProductVariant[];
	hierarchicalVariants?: HierarchicalVariant[];
	variantHierarchy?: string[];
	onVariantSelect?: (variant: any) => void;
}

const VariantSheetDesktop: React.FC<VariantSheetDesktopProps> = ({
	product,
	variants,
	hierarchicalVariants,
	variantHierarchy,
	onVariantSelect
}) => {
	// Always expanded in modal context
	const [selectedVariant, setSelectedVariant] = useState(variants[0]?.id || '');
	const { addToCart, cartItems, updateQuantity, removeFromCart } = useCart();
	const [addLoading, setAddLoading] = useState(false);
	const [selectedLevel1, setSelectedLevel1] = useState('');
	const [selectedLevel2, setSelectedLevel2] = useState('');
	const [selectedLevel3, setSelectedLevel3] = useState('');
	const [selectedHierarchicalVariant, setSelectedHierarchicalVariant] = useState<HierarchicalVariant | null>(null);

	const hasHierarchicalVariants = hierarchicalVariants && hierarchicalVariants.length > 0 && variantHierarchy;
	const isNonVariantProduct = !hasHierarchicalVariants && variants.length === 1 && variants[0]?.id === '';

	// Find selected variant object (legacy or hierarchical)
	let selectedVariantObj: any = null;
	let cartItem = null;
	let cartQuantity = 0;
	if (hasHierarchicalVariants) {
		selectedVariantObj = selectedHierarchicalVariant || (hierarchicalVariants && hierarchicalVariants[0]);
		cartItem = selectedVariantObj ? cartItems.find(item => item.variantSku === selectedVariantObj.sku) : null;
		cartQuantity = cartItem?.quantity || 0;
	} else {
		selectedVariantObj = variants.find(v => v.id === selectedVariant) || variants[0];
		cartItem = selectedVariantObj ? cartItems.find(item => item.variantSku === selectedVariantObj.id) : null;
		cartQuantity = cartItem?.quantity || 0;
	}

	const handleAddToCart = async () => {
		       if (!selectedVariantObj) return;
		       setAddLoading(true);
		       if (hasHierarchicalVariants) {
			       await addToCart({ ...product, ...selectedVariantObj }, 1, selectedVariantObj.sku);
		       } else {
			       await addToCart({ ...product, ...selectedVariantObj }, 1, selectedVariantObj.id);
		       }
		       setAddLoading(false);
	};
	const handleCartQuantityChange = (newQuantity: number) => {
		if (!cartItem) return;
		if (newQuantity <= 0) {
			removeFromCart(cartItem.productId, cartItem.variantSku, cartItem.variantId);
		} else {
			updateQuantity(cartItem.productId, newQuantity, cartItem.variantSku, cartItem.variantId);
		}
	};

	useEffect(() => {
		if (hasHierarchicalVariants) {
			const defaultVariant = hierarchicalVariants!.find(v => v.isDefault) || hierarchicalVariants![0];
			if (defaultVariant) {
				setSelectedLevel1(defaultVariant.attributes[variantHierarchy![0]] || '');
				setSelectedLevel2(defaultVariant.attributes[variantHierarchy![1]] || '');
				setSelectedLevel3(defaultVariant.attributes[variantHierarchy![2]] || '');
				setSelectedHierarchicalVariant(defaultVariant);
			}
		}
	}, [hierarchicalVariants, variantHierarchy, hasHierarchicalVariants]);

	// Option helpers
	const getLevel1Options = () => {
		if (!hasHierarchicalVariants) return [];
		const uniqueValues = new Set<string>();
		hierarchicalVariants!.forEach(variant => {
			const value = variant.attributes[variantHierarchy![0]];
			if (value) uniqueValues.add(value);
		});
		return Array.from(uniqueValues);
	};
	const getLevel2Options = () => {
		if (!hasHierarchicalVariants || !selectedLevel1 || !variantHierarchy || variantHierarchy.length < 2) return [];
		const uniqueValues = new Set<string>();
		hierarchicalVariants!
			.filter(variant => variant.attributes[variantHierarchy![0]] === selectedLevel1)
			.forEach(variant => {
				const value = variant.attributes[variantHierarchy![1]];
				if (value) uniqueValues.add(value);
			});
		return Array.from(uniqueValues);
	};
	const getLevel3Options = () => {
		if (!hasHierarchicalVariants || !selectedLevel1 || !selectedLevel2 || !variantHierarchy || variantHierarchy.length < 3) return [];
		const uniqueValues = new Set<string>();
		hierarchicalVariants!
			.filter(variant => 
				variant.attributes[variantHierarchy![0]] === selectedLevel1 &&
				variant.attributes[variantHierarchy![1]] === selectedLevel2
			)
			.forEach(variant => {
				const value = variant.attributes[variantHierarchy![2]];
				if (value) uniqueValues.add(value);
			});
		return Array.from(uniqueValues);
	};

	// Handlers
	const handleLevel1Change = (value: string) => {
		setSelectedLevel1(value);
		setSelectedLevel2('');
		setSelectedLevel3('');
		setSelectedHierarchicalVariant(null);
		
		// If this is a single-level hierarchy, find and select the variant immediately
		if (variantHierarchy && variantHierarchy.length === 1) {
			const variant = hierarchicalVariants!.find(v =>
				v.attributes[variantHierarchy![0]] === value
			);
			if (variant) {
				setSelectedHierarchicalVariant(variant);
				onVariantSelect?.(variant);
			}
		} else if (variantHierarchy && variantHierarchy.length >= 2) {
			// Auto-select first available level2 option
			const level2Options = new Set<string>();
			hierarchicalVariants!
				.filter(variant => variant.attributes[variantHierarchy![0]] === value)
				.forEach(variant => {
					const levelValue = variant.attributes[variantHierarchy![1]];
					if (levelValue && variant.quantity > 0) level2Options.add(levelValue);
				});
			
			if (level2Options.size > 0) {
				const firstLevel2 = Array.from(level2Options)[0];
				setSelectedLevel2(firstLevel2);
				
				// If this is a 2-level hierarchy, find and select the variant immediately
				if (variantHierarchy.length === 2) {
					const variant = hierarchicalVariants!.find(v =>
						v.attributes[variantHierarchy![0]] === value &&
						v.attributes[variantHierarchy![1]] === firstLevel2
					);
					if (variant) {
						setSelectedHierarchicalVariant(variant);
						onVariantSelect?.(variant);
					}
				} else if (variantHierarchy.length >= 3) {
					// Auto-select first available level3 option
					const level3Options = new Set<string>();
					hierarchicalVariants!
						.filter(variant => 
							variant.attributes[variantHierarchy![0]] === value &&
							variant.attributes[variantHierarchy![1]] === firstLevel2
						)
						.forEach(variant => {
							const levelValue = variant.attributes[variantHierarchy![2]];
							if (levelValue && variant.quantity > 0) level3Options.add(levelValue);
						});
					
					if (level3Options.size > 0) {
						const firstLevel3 = Array.from(level3Options)[0];
						setSelectedLevel3(firstLevel3);
						
						const variant = hierarchicalVariants!.find(v =>
							v.attributes[variantHierarchy![0]] === value &&
							v.attributes[variantHierarchy![1]] === firstLevel2 &&
							v.attributes[variantHierarchy![2]] === firstLevel3
						);
						if (variant) {
							setSelectedHierarchicalVariant(variant);
							onVariantSelect?.(variant);
						}
					}
				}
			}
		}
	};
	const handleLevel2Change = (value: string) => {
		setSelectedLevel2(value);
		setSelectedLevel3('');
		setSelectedHierarchicalVariant(null);
		
		// If this is a 2-level hierarchy, find and select the variant immediately
		if (variantHierarchy && variantHierarchy.length === 2) {
			const variant = hierarchicalVariants!.find(v =>
				v.attributes[variantHierarchy![0]] === selectedLevel1 &&
				v.attributes[variantHierarchy![1]] === value
			);
			if (variant) {
				setSelectedHierarchicalVariant(variant);
				onVariantSelect?.(variant);
			}
		} else if (variantHierarchy && variantHierarchy.length >= 3) {
			// Auto-select first available level3 option
			const level3Options = new Set<string>();
			hierarchicalVariants!
				.filter(variant => 
					variant.attributes[variantHierarchy![0]] === selectedLevel1 &&
					variant.attributes[variantHierarchy![1]] === value
				)
				.forEach(variant => {
					const levelValue = variant.attributes[variantHierarchy![2]];
					if (levelValue && variant.quantity > 0) level3Options.add(levelValue);
				});
			
			if (level3Options.size > 0) {
				const firstLevel3 = Array.from(level3Options)[0];
				setSelectedLevel3(firstLevel3);
				
				const variant = hierarchicalVariants!.find(v =>
					v.attributes[variantHierarchy![0]] === selectedLevel1 &&
					v.attributes[variantHierarchy![1]] === value &&
					v.attributes[variantHierarchy![2]] === firstLevel3
				);
				if (variant) {
					setSelectedHierarchicalVariant(variant);
					onVariantSelect?.(variant);
				}
			}
		}
	};
	const handleLevel3Change = (value: string) => {
		setSelectedLevel3(value);
		const variant = hierarchicalVariants!.find(v =>
			v.attributes[variantHierarchy![0]] === selectedLevel1 &&
			v.attributes[variantHierarchy![1]] === selectedLevel2 &&
			v.attributes[variantHierarchy![2]] === value
		);
		if (variant) {
			setSelectedHierarchicalVariant(variant);
			onVariantSelect?.(variant);
		}
	};

	// Color/non-color split for legacy variants
	const displayVariants = isNonVariantProduct ? [] : variants;
	const colorVariants = displayVariants.filter((variant) => {
		return (variant as any).attributes &&
			Object.keys((variant as any).attributes).some(key =>
				key.toLowerCase().includes('color') || key.toLowerCase().includes('colour'));
	});
	const nonColorVariants = displayVariants.filter((variant) => !colorVariants.includes(variant));

	// Number of options

	return (
		<div className="variant-sheet-desktop">
			<div className="variant-sheet-expanded">
				       {/* Variant rendering logic above */}
				       {/* Hierarchical Variant UI */}
				       {hasHierarchicalVariants ? (
					       <div className="variant-hierarchical-section">
						       {/* Level 1 */}
						       <div className="variant-level">
							       <h4>{variantHierarchy![0]}</h4>
							       <div className="variant-options">
								       {getLevel1Options().map((option) => {
									       const hasAvailableVariants = hierarchicalVariants!.some(variant =>
										       variant.attributes[variantHierarchy![0]] === option && variant.quantity > 0
									       );
									       return (
										       <button
											       key={option}
											       className={`variant-option ${selectedLevel1 === option ? 'selected' : ''} ${!hasAvailableVariants ? 'variant-option-disabled' : ''}`}
											       onClick={() => hasAvailableVariants && handleLevel1Change(option)}
											       disabled={!hasAvailableVariants}
										       >
											       {option}
										       </button>
									       );
								       })}
							       </div>
						       </div>
						       {/* Level 2 */}
						       {selectedLevel1 && variantHierarchy && variantHierarchy.length >= 2 && getLevel2Options().length > 0 && (
							       <div className="variant-level">
								       <h4>{variantHierarchy![1]}</h4>
								       <div className="variant-options">
									       {getLevel2Options().map((option) => {
										       const hasAvailableVariants = hierarchicalVariants!.some(variant =>
											       variant.attributes[variantHierarchy![0]] === selectedLevel1 &&
											       variant.attributes[variantHierarchy![1]] === option &&
											       variant.quantity > 0
										       );
										       return (
											       <button
												       key={option}
												       className={`variant-option ${selectedLevel2 === option ? 'selected' : ''} ${!hasAvailableVariants ? 'variant-option-disabled' : ''}`}
												       onClick={() => hasAvailableVariants && handleLevel2Change(option)}
												       disabled={!hasAvailableVariants}
											       >
												       {option}
											       </button>
										       );
									       })}
								       </div>
							       </div>
						       )}
						       {/* Level 3 */}
						       {selectedLevel1 && selectedLevel2 && variantHierarchy && variantHierarchy.length >= 3 && getLevel3Options().length > 0 && (
							       <div className="variant-level">
								       <h4>{variantHierarchy![2]}</h4>
								       <div className="variant-options">
									       {getLevel3Options().map((option) => {
										       const variant = hierarchicalVariants!.find(v =>
											       v.attributes[variantHierarchy![0]] === selectedLevel1 &&
											       v.attributes[variantHierarchy![1]] === selectedLevel2 &&
											       v.attributes[variantHierarchy![2]] === option
										       );
										       const hasQuantity = variant && variant.quantity > 0;
										       return (
											       <button
												       key={option}
												       className={`variant-option ${selectedLevel3 === option ? 'selected' : ''} ${!hasQuantity ? 'variant-option-disabled' : ''}`}
												       onClick={() => hasQuantity && handleLevel3Change(option)}
												       disabled={!hasQuantity}
											       >
												       {option}
											       </button>
										       );
									       })}
								       </div>
							       </div>
						       )}
					       </div>
				       ) : (
					       // Legacy variant UI
					       <>
						       {/* Color Variants */}
						       {colorVariants.length > 0 && (
							       <div className="variant-options">
								       {colorVariants.map((variant, idx) => {
									       let displayImage = (variant as any).variantImages?.[0] || (variant as any).variantImage;
									       if (idx === 0) {
										       displayImage = displayImage || '';
									       } else if (!displayImage || displayImage.trim() === '') {
										       displayImage = '/api/placeholder/80/80';
									       }
									       const hasQuantity = variant.quantity === undefined || variant.quantity > 0;
									       return (
										       <div
											       key={variant.id}
											       className={`variant-option variant-color ${selectedVariant === variant.id ? 'selected' : ''} ${!hasQuantity ? 'variant-option-disabled' : ''}`}
											       onClick={() => {
												       if (hasQuantity) {
													       setSelectedVariant(variant.id);
													       onVariantSelect?.(variant);
												       }
											       }}
										       >
											       <div className="variant-image-container">
												       <img 
													       src={displayImage} 
													       alt={variant.size}
													       className="variant-image"
												       />
											       </div>
										       </div>
									       );
								       })}
							       </div>
						       )}
						       {/* Non-Color Variants */}
						       {nonColorVariants.length > 0 && (
							       <div className="variant-options">
								       {nonColorVariants.map((variant) => {
									       const hasQuantity = variant.quantity === undefined || variant.quantity > 0;
									       return (
										       <div
											       key={variant.id}
											       className={`variant-option variant-size-option ${selectedVariant === variant.id ? 'selected' : ''} ${!hasQuantity ? 'variant-option-disabled' : ''}`}
											       onClick={() => {
												       if (hasQuantity) {
													       setSelectedVariant(variant.id);
													       onVariantSelect?.(variant);
												       }
											       }}
										       >
											       <div className="size-variant-content">
												       <div className="size-variant-header">{variant.size}</div>
												       <div className="size-variant-body">
													       <div className="size-variant-price">₹ {Math.round(variant.price)}</div>
												       </div>
											       </div>
										       </div>
									       );
								       })}
							       </div>
						       )}
					       </>
				       )}

				       {/* Price and Add to Cart - now always below variants */}
				       {selectedVariantObj && (
					       <div className="modal-price-add"style={{ display: 'flex', marginTop: 24, }}>
						       <div style={{ fontWeight: 600, fontSize: 18 }}>
							       ₹ {Math.round(selectedVariantObj.price)}
						       </div>
						       {cartItem ? (
							       <div className="cart-quantity-controls">
								       <button
									       className="cart-quantity-btn"
									       onClick={() => handleCartQuantityChange(cartQuantity - 1)}
									       disabled={cartQuantity <= 0}
								       >
									       -
								       </button>
								       <span className="cart-quantity-display">{cartQuantity}</span>
								       <button
									       className="cart-quantity-btn"
									       onClick={() => handleCartQuantityChange(cartQuantity + 1)}
								       >
									       +
								       </button>
							       </div>
						       ) : (
							       <button onClick={handleAddToCart} disabled={addLoading} className="modal-add-to-cart-btn">
								       {addLoading ? 'Adding...' : 'Add to Cart'}
							       </button>
						       )}
					       </div>
				       )}
			</div>
		</div>
	);
};

export default VariantSheetDesktop;
